import React from 'react';
import { Helmet } from 'react-helmet-async';
import { LifeBuoy, Mail, MessageCircle, BookOpen, CreditCard, RefreshCw, Bug } from 'lucide-react';

const CONTACT_EMAIL = 'info@wallstreetle.com';

interface TopicCardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const TopicCard: React.FC<TopicCardProps> = ({ icon, title, children }) => (
  <div className="rounded-xl border border-border bg-card p-6 space-y-3">
    <div className="flex items-center gap-3">
      <div className="rounded-lg bg-emerald-100 dark:bg-emerald-500/20 p-2 text-emerald-600 dark:text-emerald-400">
        {icon}
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
    <div className="text-sm text-muted-foreground space-y-2">{children}</div>
  </div>
);

export const SupportPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Support</title>
        <meta
          name="description"
          content="Get help with Wallstreetle — the daily stock guessing game. Contact support, find answers to common questions, and manage your subscription."
        />
        <link rel="canonical" href="https://wallstreetle.com/support" />
        <meta property="og:title" content="Wallstreetle | Support" />
        <meta
          property="og:description"
          content="Get help with Wallstreetle. Contact our support team or find answers to common questions."
        />
        <meta property="og:url" content="https://wallstreetle.com/support" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3">
              <LifeBuoy className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-3xl font-bold text-foreground">Support</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  We're here to help — usually reply within 24 hours
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
          {/* Contact CTA */}
          <div className="rounded-xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Still need help?</p>
              <p className="text-sm text-muted-foreground">
                Email us and we'll get back to you as soon as possible.
              </p>
            </div>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-5 py-2.5 text-sm transition-colors whitespace-nowrap"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>
          </div>

          {/* Topic cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <TopicCard icon={<BookOpen className="h-5 w-5" />} title="How to play">
              <p>
                Each day a new stock is hidden across up to 9 categories. Use the financial chart
                and metric clues to guess the company in as few tries as possible.
              </p>
              <a href="/how-to-play" className="text-primary hover:underline font-medium">
                Read the full guide →
              </a>
            </TopicCard>

            <TopicCard icon={<CreditCard className="h-5 w-5" />} title="Subscription & billing">
              <p>
                Premium unlocks 6 additional stock categories. You can manage or cancel your
                subscription at any time from the Settings page.
              </p>
              <p>
                <a href="/settings/subscription" className="text-primary hover:underline font-medium">
                  Manage subscription →
                </a>
              </p>
              <p className="text-xs">
                Purchased on iOS? Manage through{' '}
                <strong>Settings → Apple ID → Subscriptions</strong> on your device, or via the
                RevenueCat Customer Center in the app.
              </p>
            </TopicCard>

            <TopicCard icon={<RefreshCw className="h-5 w-5" />} title="Daily reset & streaks">
              <p>
                Games reset at <strong>2:00 AM UTC</strong> each day. If your streak looks wrong
                after the reset, try refreshing the page or reopening the app.
              </p>
              <p>
                Streak data is tied to your account — sign in to preserve it across devices.
              </p>
            </TopicCard>

            <TopicCard icon={<Bug className="h-5 w-5" />} title="Bug reports">
              <p>
                Found something broken? Please email us with a brief description of what happened
                and which device / browser you're using. Screenshots are very helpful.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=Bug report`}
                className="text-primary hover:underline font-medium"
              >
                Report a bug →
              </a>
            </TopicCard>

            <TopicCard icon={<MessageCircle className="h-5 w-5" />} title="Community">
              <p>Join the community to share results, chat about the daily stock, and get tips.</p>
              <ul className="space-y-1">
                <li>
                  <a
                    href="https://discord.gg/8xnYDataFw"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    Discord server →
                  </a>
                </li>
                <li>
                  <a
                    href="https://reddit.com/r/wallstreetle"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline font-medium"
                  >
                    r/wallstreetle →
                  </a>
                </li>
              </ul>
            </TopicCard>

            <TopicCard icon={<BookOpen className="h-5 w-5" />} title="FAQ">
              <p>Browse answers to the most common questions about gameplay, accounts, and features.</p>
              <a href="/faq" className="text-primary hover:underline font-medium">
                View FAQ →
              </a>
            </TopicCard>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-muted-foreground">
            Wallstreetle is a game for entertainment purposes only and does not constitute financial
            advice.
          </p>
        </div>
      </div>
    </>
  );
};

export default SupportPage;
